import { useState } from "react";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Italic,
  Palette,
  Trash2,
  Type,
} from "lucide-react";
import type { TimelineTextOverlay, TimelineAction } from "@scroll/shared-types/timeline";
import { OVERLAY_COLORS, OVERLAY_FONTS } from "@scroll/shared-types/overlays";

type InspectorTab = "text" | "font" | "color" | "delete";

interface TextOverlayInspectorProps {
  overlay: TimelineTextOverlay;
  onDispatch: (action: TimelineAction) => void;
  onClose: () => void;
  onDelete: () => void;
}

export default function TextOverlayInspector({
  overlay,
  onDispatch,
  onClose,
  onDelete,
}: TextOverlayInspectorProps) {
  const [activeTab, setActiveTab] = useState<InspectorTab>("text");

  const patch = (changes: Partial<TimelineTextOverlay>) => {
    onDispatch({ type: "UPDATE_TEXT_OVERLAY", id: overlay.id, patch: changes });
  };

  return (
    <div className="clip-inspector">
      <div className="clip-inspector-header">
        <span className="clip-inspector-title">Text Properties</span>
        <button
          className="timeline-toolbar-btn"
          onClick={onClose}
          aria-label="Close text overlay inspector"
        >
          &times;
        </button>
      </div>

      <div className="clip-inspector-tabs" role="tablist" aria-label="Text overlay tools">
        <button
          className={`clip-inspector-tab ${activeTab === "text" ? "active" : ""}`}
          type="button"
          role="tab"
          aria-selected={activeTab === "text"}
          onClick={() => setActiveTab("text")}
        >
          <Type size={14} />
          <span>Text</span>
        </button>
        <button
          className={`clip-inspector-tab ${activeTab === "font" ? "active" : ""}`}
          type="button"
          role="tab"
          aria-selected={activeTab === "font"}
          onClick={() => setActiveTab("font")}
        >
          <Bold size={14} />
          <span>Font</span>
          <small>{overlay.fontFamily}</small>
        </button>
        <button
          className={`clip-inspector-tab ${activeTab === "color" ? "active" : ""}`}
          type="button"
          role="tab"
          aria-selected={activeTab === "color"}
          onClick={() => setActiveTab("color")}
        >
          <Palette size={14} />
          <span>Color</span>
        </button>
        <button
          className={`clip-inspector-tab ${activeTab === "delete" ? "active" : ""}`}
          type="button"
          role="tab"
          aria-selected={activeTab === "delete"}
          onClick={() => setActiveTab("delete")}
        >
          <Trash2 size={14} />
          <span>Delete</span>
        </button>
      </div>

      {activeTab === "text" && (
        <div className="clip-inspector-section">
          <div className="clip-inspector-section-header">
            <Type size={14} />
            <span>Content</span>
          </div>
          <textarea
            className="text-inspector-textarea"
            value={overlay.text}
            onChange={(e) => patch({ text: e.target.value })}
            rows={3}
            placeholder="Overlay text"
            aria-label="Overlay text content"
          />

          <div className="clip-inspector-section-header clip-inspector-section-header--spaced">
            <AlignCenter size={14} />
            <span>Alignment</span>
          </div>
          <div className="text-inspector-row" role="group" aria-label="Text alignment">
            <button
              className={`text-inspector-toggle ${overlay.textAlign === "left" ? "active" : ""}`}
              onClick={() => patch({ textAlign: "left" })}
              aria-pressed={overlay.textAlign === "left"}
              aria-label="Align left"
            >
              <AlignLeft size={14} />
            </button>
            <button
              className={`text-inspector-toggle ${overlay.textAlign === "center" ? "active" : ""}`}
              onClick={() => patch({ textAlign: "center" })}
              aria-pressed={overlay.textAlign === "center"}
              aria-label="Align center"
            >
              <AlignCenter size={14} />
            </button>
            <button
              className={`text-inspector-toggle ${overlay.textAlign === "right" ? "active" : ""}`}
              onClick={() => patch({ textAlign: "right" })}
              aria-pressed={overlay.textAlign === "right"}
              aria-label="Align right"
            >
              <AlignRight size={14} />
            </button>
            <button
              className={`text-inspector-toggle ${overlay.bold ? "active" : ""}`}
              onClick={() => patch({ bold: !overlay.bold })}
              aria-pressed={overlay.bold}
              aria-label="Bold"
            >
              <Bold size={14} />
            </button>
            <button
              className={`text-inspector-toggle ${overlay.italic ? "active" : ""}`}
              onClick={() => patch({ italic: !overlay.italic })}
              aria-pressed={overlay.italic}
              aria-label="Italic"
            >
              <Italic size={14} />
            </button>
          </div>
        </div>
      )}

      {activeTab === "font" && (
        <div className="clip-inspector-section">
          <div className="clip-inspector-section-header">
            <Type size={14} />
            <span>Font</span>
          </div>
          <div className="text-inspector-font-grid">
            {OVERLAY_FONTS.map((font) => (
              <button
                key={font}
                className={`text-inspector-font-btn ${overlay.fontFamily === font ? "active" : ""}`}
                onClick={() => patch({ fontFamily: font })}
                aria-pressed={overlay.fontFamily === font}
              >
                {font}
              </button>
            ))}
          </div>

          <div className="clip-inspector-section-header clip-inspector-section-header--spaced">
            <Type size={14} />
            <span>Size</span>
          </div>
          <input
            type="range"
            className="clip-inspector-slider"
            min={12}
            max={96}
            step={1}
            value={overlay.fontSize}
            onChange={(e) => patch({ fontSize: parseInt(e.target.value, 10) })}
            aria-label="Font size"
          />
          <div className="clip-inspector-slider-labels">
            <span>12</span>
            <span>{overlay.fontSize}</span>
            <span>96</span>
          </div>
        </div>
      )}

      {activeTab === "color" && (
        <div className="clip-inspector-section">
          <div className="clip-inspector-section-header">
            <Palette size={14} />
            <span>Text Color</span>
          </div>
          <div className="text-inspector-color-grid" role="radiogroup" aria-label="Text color">
            {OVERLAY_COLORS.map((color) => (
              <button
                key={color}
                className={`text-inspector-color-swatch ${overlay.color === color ? "active" : ""}`}
                style={{ background: color }}
                onClick={() => patch({ color })}
                role="radio"
                aria-checked={overlay.color === color}
                aria-label={`Color ${color}`}
              />
            ))}
          </div>

          <div className="clip-inspector-section-header clip-inspector-section-header--spaced">
            <Palette size={14} />
            <span>Background</span>
          </div>
          <div className="text-inspector-color-grid" role="radiogroup" aria-label="Background color">
            <button
              className={`text-inspector-color-swatch transparent ${!overlay.backgroundColor ? "active" : ""}`}
              onClick={() => patch({ backgroundColor: undefined })}
              role="radio"
              aria-checked={!overlay.backgroundColor}
              aria-label="No background"
            />
            {OVERLAY_COLORS.map((color) => (
              <button
                key={color}
                className={`text-inspector-color-swatch ${overlay.backgroundColor === color ? "active" : ""}`}
                style={{ background: color }}
                onClick={() => patch({ backgroundColor: color })}
                role="radio"
                aria-checked={overlay.backgroundColor === color}
                aria-label={`Background ${color}`}
              />
            ))}
          </div>

          <div className="clip-inspector-section-header clip-inspector-section-header--spaced">
            <Palette size={14} />
            <span>Opacity</span>
          </div>
          <input
            type="range"
            className="clip-inspector-slider"
            min={0}
            max={1}
            step={0.05}
            value={overlay.opacity}
            onChange={(e) => patch({ opacity: parseFloat(e.target.value) })}
            aria-label="Overlay opacity"
          />
          <div className="clip-inspector-slider-labels">
            <span>0%</span>
            <span>{Math.round(overlay.opacity * 100)}%</span>
            <span>100%</span>
          </div>
        </div>
      )}

      {activeTab === "delete" && (
        <div className="clip-inspector-section">
          <button className="clip-inspector-delete-btn" onClick={onDelete}>
            <Trash2 size={14} />
            <span>Delete Text Overlay</span>
          </button>
        </div>
      )}
    </div>
  );
}
